import { useMemo } from 'react';
import { useSearchParams } from 'react-router';
import type {
  EmailSignupPreviewConfig,
  SponsorshipPreviewConfig,
} from '@/lib/embed/emailSignupPreview';
import {
  normalizeEndScreenEmailSignupContent,
  normalizeEndScreenSponsorshipCtaContent,
} from '@/lib/embed/endScreenContent';
import type {
  EndScreenEmailSignupContent,
  EndScreenSponsorshipCtaContent,
} from '@/lib/embed/endScreenContent';
import { isEmbedEditorPreviewSearch } from '@/lib/embed/embedEditorPreview';
import type { ResolvedQuizSponsorsStylePresetConfiguration } from '@/lib/domain/sponsorsStylePreset';

interface UseEmbedCompletionContentOptions {
  previewEmailSignup?: EmailSignupPreviewConfig | null;
  previewSponsorship?: SponsorshipPreviewConfig | null;
  effectiveConfiguration?: ResolvedQuizSponsorsStylePresetConfiguration;
}

interface UseEmbedCompletionContentResult {
  isPartnerEmbed: boolean;
  isPreviewMode: boolean;
  resolvedEmailSignupContent: EndScreenEmailSignupContent | null;
  resolvedSponsorshipContent: SponsorshipPreviewConfig | null;
  resolvedSponsorshipCtaContent: EndScreenSponsorshipCtaContent | null;
}

export function useEmbedCompletionContent({
  previewEmailSignup = null,
  previewSponsorship = null,
  effectiveConfiguration,
}: UseEmbedCompletionContentOptions): UseEmbedCompletionContentResult {
  const [searchParams] = useSearchParams();
  const embedPartnerId = searchParams.get('partner_id') ?? undefined;
  const embedOrigin = searchParams.get('origin') ?? undefined;
  const isPartnerEmbed = Boolean(embedPartnerId && embedOrigin);
  const isEmbedEditorPreview = isEmbedEditorPreviewSearch(searchParams.toString());
  const isPreviewMode =
    isEmbedEditorPreview || previewEmailSignup !== null || previewSponsorship !== null;

  const presetEmailSignup = useMemo<EmailSignupPreviewConfig | null>(() => {
    if (!effectiveConfiguration) return null;
    const emailSignup = effectiveConfiguration.emailSignup;
    return {
      enabled: emailSignup.enabled,
      signup_type: emailSignup.signupType,
      title: emailSignup.title,
      description: emailSignup.description,
      button_cta: emailSignup.buttonCta,
      confirmation_message: emailSignup.confirmationMessage,
      signup_url: emailSignup.signupUrl,
      embed_code: emailSignup.embedCode,
    };
  }, [effectiveConfiguration]);

  const presetSponsorship = useMemo<SponsorshipPreviewConfig | null>(() => {
    if (!effectiveConfiguration) return null;
    const sponsorship = effectiveConfiguration.sponsorship;
    return {
      sponsored_by_enabled: sponsorship.sponsoredByEnabled,
      sponsored_by_text: sponsorship.sponsoredByText,
      sponsored_by_color: sponsorship.sponsoredByColor,
      sponsored_by_url: sponsorship.footerUrl,
      logo_storage_bucket: sponsorship.logoStorageBucket,
      logo_storage_path: sponsorship.logoStoragePath,
      cta_enabled: sponsorship.ctaEnabled,
      cta_image_storage_bucket: sponsorship.ctaImageStorageBucket,
      cta_image_storage_path: sponsorship.ctaImageStoragePath,
      cta_image_aspect_ratio: sponsorship.ctaImageAspectRatio,
      cta_tagline: sponsorship.ctaTagline,
      cta_title: sponsorship.ctaTitle,
      cta_description: sponsorship.ctaDescription,
      cta_button_text: sponsorship.ctaButtonText,
      cta_url: sponsorship.ctaUrl,
    };
  }, [effectiveConfiguration]);

  const resolvedEmailSignupContent = useMemo(
    () =>
      normalizeEndScreenEmailSignupContent(isPreviewMode ? previewEmailSignup : presetEmailSignup, {
        allowIncomplete: isPreviewMode,
      }),
    [isPreviewMode, presetEmailSignup, previewEmailSignup]
  );

  const resolvedSponsorshipCtaContent = useMemo(
    () =>
      normalizeEndScreenSponsorshipCtaContent(
        isPreviewMode ? previewSponsorship : presetSponsorship
      ),
    [isPreviewMode, presetSponsorship, previewSponsorship]
  );
  const resolvedSponsorshipContent = isPreviewMode ? previewSponsorship : presetSponsorship;

  return {
    isPartnerEmbed,
    isPreviewMode,
    resolvedEmailSignupContent,
    resolvedSponsorshipContent,
    resolvedSponsorshipCtaContent,
  };
}
